import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import budgetService from '../../services/budgetService';
import projectService from '../../services/projectService';

const BudgetForm = ({ budget, onSubmit, onCancel }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [formData, setFormData] = useState({
    project_id: budget?.project_id || '',
    category: budget?.category || 'Labor',
    budget_allocated: budget?.budget_allocated || '',
    budget_spent: budget?.budget_spent || '0',
    fiscal_year: budget?.fiscal_year || new Date().getFullYear(),
    notes: budget?.notes || ''
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const data = await projectService.getAllProjects();
      setProjects(data.projects || []);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.project_id) {
      newErrors.project_id = 'Project is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.budget_allocated || parseFloat(formData.budget_allocated) <= 0) {
      newErrors.budget_allocated = 'Budget allocated must be greater than 0';
    }
    if (parseFloat(formData.budget_spent) < 0) {
      newErrors.budget_spent = 'Budget spent cannot be negative';
    }
    if (!formData.fiscal_year) {
      newErrors.fiscal_year = 'Fiscal year is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        budget_allocated: parseFloat(formData.budget_allocated),
        budget_spent: parseFloat(formData.budget_spent),
        fiscal_year: parseInt(formData.fiscal_year)
      };

      if (onSubmit) {
        await onSubmit(submitData);
      } else {
        if (budget) {
          await budgetService.updateBudget(budget.id, submitData);
          toast.success('Budget updated successfully');
        } else {
          await budgetService.createBudget(submitData);
          toast.success('Budget created successfully');
        }
        navigate('/app/budgets');
      }
    } catch (error) {
      console.error('Error saving budget:', error);
      toast.error(error.response?.data?.error || 'Failed to save budget');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/app/budgets');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow space-y-6">
        {/* Project Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Project <span className="text-red-500">*</span>
          </label>
          <select
            name="project_id"
            value={formData.project_id}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.project_id ? 'border-red-500' : 'border-gray-300'
            }`}
            disabled={!!budget}
          >
            <option value="">Select a project</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>
                {project.name} ({project.code})
              </option>
            ))}
          </select>
          {errors.project_id && (
            <p className="mt-1 text-sm text-red-500">{errors.project_id}</p>
          )}
        </div>

        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.category ? 'border-red-500' : 'border-gray-300'
            }`}
          >
            {budgetService.getCategories().map(cat => (
              <option key={cat.value} value={cat.value}>
                {cat.icon} {cat.label}
              </option>
            ))}
          </select>
          {errors.category && (
            <p className="mt-1 text-sm text-red-500">{errors.category}</p>
          )}
        </div>
          </div>
        {/* Budget Allocated and Spent */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget Allocated <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="budget_allocated"
              value={formData.budget_allocated}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.budget_allocated ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.budget_allocated && (
              <p className="mt-1 text-sm text-red-500">{errors.budget_allocated}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Budget Spent
            </label>
            <input
              type="number"
              name="budget_spent"
              value={formData.budget_spent}
              onChange={handleChange}
              placeholder="0.00"
              step="0.01"
              min="0"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.budget_spent ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.budget_spent && (
              <p className="mt-1 text-sm text-red-500">{errors.budget_spent}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Current spent amount (updated automatically with expenses)
            </p>
          </div>
       

        {/* Fiscal Year */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Fiscal Year <span className="text-red-500">*</span>
          </label>
          <input
            type="number"
            name="fiscal_year"
            value={formData.fiscal_year}
            onChange={handleChange}
            placeholder="2024"
            min="2000"
            max="2100"
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
              errors.fiscal_year ? 'border-red-500' : 'border-gray-300'
            }`}
          />
          {errors.fiscal_year && (
            <p className="mt-1 text-sm text-red-500">{errors.fiscal_year}</p>
          )}
        </div>
 </div>
        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows="4"
            placeholder="Add any additional notes about this budget..."
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Budget Summary */}
        {formData.budget_allocated && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-blue-900 mb-2">Budget Summary</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Allocated</p>
                <p className="font-semibold text-gray-900">
                  {budgetService.formatCurrency(formData.budget_allocated)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Spent</p>
                <p className="font-semibold text-gray-900">
                  {budgetService.formatCurrency(formData.budget_spent)}
                </p>
              </div>
              <div>
                <p className="text-gray-600">Remaining</p>
                <p className={`font-semibold ${
                  (parseFloat(formData.budget_allocated) - parseFloat(formData.budget_spent)) >= 0
                    ? 'text-green-600'
                    : 'text-red-600'
                }`}>
                  {budgetService.formatCurrency(
                    parseFloat(formData.budget_allocated) - parseFloat(formData.budget_spent)
                  )}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={handleCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          disabled={loading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={loading}
        >
          {loading ? 'Saving...' : budget ? 'Update Budget' : 'Create Budget'}
        </button>
      </div>
    </form>
  );
};

export default BudgetForm;

